import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Shield, CheckCircle2, Award, MessageCircle, Instagram, Play, Heart, Users, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { projects as mockProjects, differentials, testimonials as mockTestimonials, workGallery as mockGallery, companyInfo, faqs as mockFaqs } from '../mock';
import api from '../services/api';

const Home = () => {
  const [projects, setProjects] = useState(mockProjects);
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [workGallery, setWorkGallery] = useState(mockGallery);
  const [faqs, setFaqs] = useState(mockFaqs);

  useEffect(() => {
    // Fetch data from API with fallback to mock
    const loadData = async () => {
      const [projectsData, testimonialsData, galleryData, faqsData] = await Promise.all([
        api.getProjects(mockProjects),
        api.getTestimonials(mockTestimonials),
        api.getGallery(mockGallery),
        api.getFAQs(mockFaqs)
      ]);
      
      if (projectsData.length > 0) setProjects(projectsData);
      if (testimonialsData.length > 0) setTestimonials(testimonialsData);
      if (galleryData.length > 0) setWorkGallery(galleryData);
      if (faqsData.length > 0) setFaqs(faqsData);
    };
    
    loadData();
  }, []);

  return (
    <div className="mt-20">
      {/* Hero Section with Brand Impact */}
      <section className="relative bg-gradient-to-br from-[#15448A] via-[#0d2f5e] to-[#15448A] overflow-hidden min-h-[85vh] flex items-center">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#F47A20]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        
        <div className="container-custom py-16 lg:py-24 relative w-full">
          {/* Logo Grande em Destaque */}
          <div className="text-center mb-16">
            <div className="inline-block animate-fadeIn">
              <div className="relative">
                {/* Glow Effect Behind Logo */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#F47A20] to-[#15448A] rounded-3xl blur-2xl opacity-50 scale-110"></div>
                
                {/* Logo Container */}
                <div className="relative bg-white rounded-3xl p-10 lg:p-14 shadow-2xl inline-block transform hover:scale-105 transition-all duration-500 animate-pulse-glow animate-float">
                  <img 
                    src="/logo-luvcorp.png" 
                    alt="LUVCORP Soluções Prediais" 
                    className="h-36 md:h-48 lg:h-56 w-auto mx-auto drop-shadow-lg"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-10 space-y-6">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight animate-slideUp">
                <span className="text-[#F47A20] drop-shadow-lg">Revitalizamos</span> o Seu Sonho
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto animate-slideUp" style={{animationDelay: '0.2s'}}>
                Especialistas em Reforma de Fachadas e Manutenção Predial no Rio de Janeiro
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto mb-8 animate-slideUp" style={{animationDelay: '0.4s'}}>
            <div className="text-center p-4 md:p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="text-3xl md:text-5xl font-bold text-[#F47A20]">45+</div>
              <div className="text-sm md:text-base text-blue-100 mt-1">Condomínios</div>
            </div>
            <div className="text-center p-4 md:p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="text-3xl md:text-5xl font-bold text-[#F47A20]">5+</div>
              <div className="text-sm md:text-base text-blue-100 mt-1">Anos de Experiência</div>
            </div>
            <div className="text-center p-4 md:p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="text-3xl md:text-5xl font-bold text-[#F47A20]">95%</div>
              <div className="text-sm md:text-base text-blue-100 mt-1">Satisfação</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slideUp pb-16" style={{animationDelay: '0.6s'}}>
            <Link to="/contato">
              <Button size="lg" className="bg-[#F47A20] hover:bg-[#e66a10] text-white font-bold shadow-2xl hover:shadow-orange-500/30 text-lg px-10 py-7 rounded-xl transition-all duration-300 hover:scale-105 border-2 border-[#F47A20]">
                Solicitar Orçamento Gratuito
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/projetos">
              <Button size="lg" className="bg-white text-[#15448A] hover:bg-gray-100 font-bold shadow-2xl text-lg px-10 py-7 rounded-xl transition-all duration-300 hover:scale-105 border-2 border-white">
                Ver Projetos Realizados
              </Button>
            </Link>
          </div>
        </div>

        {/* Straight Divider */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-white"></div>
      </section>

      {/* About Section Brief */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-[#15448A]/10 text-[#15448A] hover:bg-[#15448A]/20 px-4 py-1">
                Quem Somos
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900">
                Excelência em <span className="text-[#F47A20]">Soluções Prediais</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                A LUVCORP nasceu da paixão por transformar e revitalizar edifícios. Com mais de 5 anos de experiência no mercado carioca, 
                nos especializamos em reformas de fachadas, manutenção predial e soluções completas para condomínios.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Trabalhamos com <strong>equipe 100% própria</strong>, equipamentos de ponta e materiais de alta qualidade, 
                garantindo resultados que superam as expectativas dos nossos clientes.
              </p>
              <Link to="/quem-somos">
                <Button variant="outline" className="border-2 border-[#15448A] text-[#15448A] hover:bg-[#15448A]/5">
                  Conhecer Nossa História
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-[#15448A] to-[#0d2f5e] p-6 rounded-2xl text-white">
                  <Shield className="mb-3" size={32} />
                  <h3 className="font-bold text-lg">Seguro Completo</h3>
                  <p className="text-blue-100 text-sm mt-1">Proteção total em todas as obras</p>
                </div>
                <div className="bg-gradient-to-br from-[#F47A20] to-[#e66a10] p-6 rounded-2xl text-white">
                  <Award className="mb-3" size={32} />
                  <h3 className="font-bold text-lg">CREA Registrado</h3>
                  <p className="text-orange-100 text-sm mt-1">Responsabilidade técnica garantida</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="bg-gray-100 p-6 rounded-2xl">
                  <Building2 className="mb-3 text-[#15448A]" size={32} />
                  <h3 className="font-bold text-lg text-gray-900">Equipe Própria</h3>
                  <p className="text-gray-600 text-sm mt-1">Profissionais treinados e qualificados</p>
                </div>
                <div className="bg-gray-100 p-6 rounded-2xl">
                  <CheckCircle2 className="mb-3 text-[#15448A]" size={32} />
                  <h3 className="font-bold text-lg text-gray-900">Garantia Estendida</h3>
                  <p className="text-gray-600 text-sm mt-1">Compromisso com a qualidade</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work Gallery Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#F47A20]/10 text-[#F47A20] hover:bg-[#F47A20]/20 mb-4">
              Nossos Trabalhos
            </Badge>
            <h2 className="text-4xl font-bold mb-6">
              Excelência em Cada Projeto Executado
            </h2>
            <p className="text-lg text-gray-600">
              Confira alguns dos nossos trabalhos realizados com qualidade, segurança e profissionalismo.
            </p>
          </div>
          
          {/* Gallery Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {workGallery.map((work) => (
              <div key={work.id} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500">
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={work.image}
                    alt={work.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#15448A]/90 via-[#15448A]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <Badge className="bg-[#F47A20] hover:bg-[#F47A20] text-white mb-2 text-xs">
                      {work.category}
                    </Badge>
                    <h3 className="text-base font-bold text-white">
                      {work.title}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA Button */}
          <div className="text-center">
            <div className="max-w-2xl mx-auto space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">
                Quer Ver Mais Trabalhos?
              </h3>
              <p className="text-gray-600">
                Solicite nosso portfólio completo via WhatsApp e conheça todos os nossos projetos executados com excelência.
              </p>
              <a 
                href={`https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent('Olá! Gostaria de receber o portfólio completo da LUVCORP.')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-white shadow-xl hover:shadow-2xl text-lg px-10 py-7 group">
                  <MessageCircle className="mr-3 group-hover:scale-110 transition-transform" size={24} />
                  Solicitar Portfólio Completo
                  <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </a>
              <p className="text-sm text-gray-500">
                Atendimento rápido via WhatsApp • Resposta em poucos minutos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#F47A20]/10 text-[#F47A20] hover:bg-[#F47A20]/20 mb-4">
              Condomínios Atendidos
            </Badge>
            <h2 className="text-4xl font-bold mb-6">
              Condomínios que Confiam na LUVCORP
            </h2>
            <p className="text-lg text-gray-600">
              Conheça os condomínios que confiam na LUVCORP. Projetos executados com excelência técnica, planejamento detalhado e compromisso com prazos e qualidade.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.slice(0, 6).map((project) => (
              <Card key={project.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#15448A]/30 bg-white">
                <CardContent className="p-8 space-y-4">
                  <div className="flex items-center justify-center">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#15448A] to-[#0d2f5e] flex items-center justify-center text-white font-bold text-2xl group-hover:scale-110 transition-transform">
                      <Building2 size={28} />
                    </div>
                  </div>
                  
                  <div className="space-y-3 text-center">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#15448A] transition-colors">
                      {project.title}
                    </h3>
                    
                    <div className="flex items-start justify-center space-x-2 text-gray-600">
                      <svg className="w-5 h-5 text-[#F47A20] mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm leading-relaxed">{project.location}</span>
                    </div>
                    
                    <div className="pt-3 border-t border-gray-200 flex justify-center">
                      <Badge variant="outline" className="border-[#15448A]/30 text-[#15448A]">
                        {project.category}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/projetos">
              <Button size="lg" className="bg-gradient-to-r from-[#15448A] to-[#0d2f5e] hover:from-[#0d2f5e] hover:to-[#15448A] text-white shadow-lg">
                Ver Todos os Condomínios
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Differentials */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-orange-100 text-[#F47A20]700 hover:bg-orange-200 mb-4">
              Por Que Escolher a LUVCORP
            </Badge>
            <h2 className="text-4xl font-bold mb-6">
              Nossos Diferenciais Competitivos
            </h2>
            <p className="text-lg text-gray-600">
              Comprometimento com excelência, segurança e satisfação total do cliente em cada projeto.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentials.map((diff) => (
              <div key={diff.id} className="p-8 rounded-xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                  <Shield size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {diff.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {diff.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-white/20 text-white hover:bg-white/30 mb-4">
              Depoimentos
            </Badge>
            <h2 className="text-4xl font-bold mb-6">
              O Que Nossos Clientes Dizem
            </h2>
            <p className="text-lg text-blue-100">
              A confiança de síndicos e administradoras é nosso maior patrimônio.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/10 backdrop-blur-md border-white/20 text-white hover:bg-white/15 transition-all">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#F47A20]400 text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-blue-50 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="pt-4 border-t border-white/20">
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-blue-200">{testimonial.role}</div>
                    <Badge className="mt-2 bg-[#F47A20]/20 text-[#F47A20]300 border-orange-400/30">
                      {testimonial.project}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <Badge className="bg-[#15448A]/10 text-[#15448A]">
                Tire Suas Dúvidas
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900">
                Perguntas Frequentes
              </h2>
              <p className="text-lg text-gray-600">
                Respostas para as dúvidas mais comuns sobre nossos serviços
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.slice(0, 10).map((faq, index) => (
                <AccordionItem 
                  key={faq.id || index} 
                  value={`faq-${index}`}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 rounded-lg bg-[#15448A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <HelpCircle size={18} className="text-[#15448A]" />
                      </div>
                      <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pl-12 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {faqs.length > 10 && (
              <div className="text-center mt-8">
                <p className="text-gray-500 text-sm">
                  E mais {faqs.length - 10} perguntas frequentes disponíveis
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="section-padding bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center text-white space-y-6 mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm mb-4">
                <Instagram size={40} className="text-white" />
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold">
                Siga-nos no Instagram!
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                Acompanhe vídeos exclusivos, bastidores das obras, dicas de manutenção predial e interaja diretamente com nossa equipe!
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* @luvcorp_ */}
              <a 
                href="https://instagram.com/luvcorp_" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#15448A] to-[#0d2f5e] flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        LC
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white group-hover:text-white/90">@luvcorp_</h3>
                        <p className="text-white/70 text-sm">Perfil Oficial LUVCORP</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <ArrowRight size={20} className="text-white" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-white/80">
                        <Building2 size={18} />
                        <span className="text-sm">Projetos e transformações incríveis</span>
                      </div>
                      <div className="flex items-center space-x-2 text-white/80">
                        <Play size={18} />
                        <span className="text-sm">Vídeos de antes e depois</span>
                      </div>
                      <div className="flex items-center space-x-2 text-white/80">
                        <CheckCircle2 size={18} />
                        <span className="text-sm">Cases de sucesso</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <Button className="w-full bg-white text-pink-600 hover:bg-white/90 font-semibold">
                        <Instagram size={18} className="mr-2" />
                        Seguir @luvcorp_
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </a>

              {/* @salveobreiros */}
              <a 
                href="https://instagram.com/salveobreiros" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        SO
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white group-hover:text-white/90">@salveobreiros</h3>
                        <p className="text-white/70 text-sm">Nossa Equipe em Ação</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <ArrowRight size={20} className="text-white" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-white/80">
                        <Users size={18} />
                        <span className="text-sm">Rotina da equipe no dia a dia</span>
                      </div>
                      <div className="flex items-center space-x-2 text-white/80">
                        <Play size={18} />
                        <span className="text-sm">Bastidores das obras</span>
                      </div>
                      <div className="flex items-center space-x-2 text-white/80">
                        <Heart size={18} />
                        <span className="text-sm">Dicas e conteúdo exclusivo</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <Button className="w-full bg-white text-pink-600 hover:bg-white/90 font-semibold">
                        <Instagram size={18} className="mr-2" />
                        Seguir @salveobreiros
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </a>
            </div>

            <div className="text-center">
              <p className="text-white/80 text-lg">
                <Heart size={18} className="inline mr-2 text-red-300" />
                Junte-se a milhares de síndicos e gestores que nos acompanham!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center text-white space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Pronto para Transformar Seu Condomínio?
            </h2>
            <p className="text-xl text-[#F47A20]50">
              Solicite uma vistoria técnica gratuita e receba um orçamento detalhado sem compromisso. Nossa equipe está pronta para atender você.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button size="lg" className="bg-white text-[#F47A20]600 hover:bg-gray-100 shadow-xl text-lg px-8 py-6">
                  Solicitar Orçamento Agora
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href={`https://wa.me/5521987361748?text=${encodeURIComponent('Olá! Gostaria de uma vistoria técnica gratuita.')}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6">
                  Falar no WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
